import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Group, Text, Card, Textarea, ActionIcon, Loader, Button } from '@mantine/core';
import { IconSend, IconRefresh, IconTrash, IconEdit, IconX, IconWand } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { currentChatAtom, currentMessagesAtom, sendMessageAtom, createChatAtom, currentChatIdAtom, appendAssistantChunkAtom, messagesMapAtom } from '../../stores/atoms/chatAtoms';
// import { toggleSidebarAtom } from '../../stores/atoms/uiAtoms';
import { wsManager } from '@/utils/wsManager';
import { currentScriptIdAtom } from '@/stores/atoms/scriptAtoms';
import MarkdownMessage from './MarkdownMessage';
import { triggerSlash } from '@/renderer/utils/triggerSlash';
import { sessionsAtom, currentSessionIdAtom, updateSessionAtom, createSessionAtom, setActiveSessionAtom } from '@/stores/atoms/sessionAtoms';
import { notifications } from '@mantine/notifications';
import { confirmDialog } from '@/utils/confirm';
import { useT } from '@/i18n';
import { languageAtom } from '@/stores/atoms/i18nAtoms';
import { novelChatSettingsAtom } from '@/stores/atoms/novelChatSettingsAtoms';
import { chatToGameMessages, createSessionForScript } from '@/renderer/utils/sessionSync';
import { authUserAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { getStoredAuthToken } from '@/utils/userApi';
import { httpFetch } from '@/renderer/utils/http';
import { getApiBase } from '@/renderer/utils/session';
const ChatWindow = ({ messageStyle = 'beautified', onUiData, onFloatsHeightChange }) => {
    const [currentChat] = useAtom(currentChatAtom);
    const [messages] = useAtom(currentMessagesAtom);
    const [, send] = useAtom(sendMessageAtom);
    const [, createChat] = useAtom(createChatAtom);
    const [currentId] = useAtom(currentChatIdAtom);
    const [draft, setDraft] = useState('');
    // header and title managed by overlay
    const endRef = useRef(null);
    const lastMsgRef = useRef(null);
    const scrollRef = useRef(null);
    const floatsRef = useRef(null);
    // Queue first outgoing text if chat id not ready yet
    const pendingFirstTextRef = useRef(null);
    // const [, toggleSidebar] = useAtom(toggleSidebarAtom);
    const [isStreaming, setIsStreaming] = useState(false);
    const [, appendAssistantChunk] = useAtom(appendAssistantChunkAtom);
    const [activeScriptId] = useAtom(currentScriptIdAtom);
    const [sessionReady, setSessionReady] = useState(false);
    // overlay navigation handled by ChatOverlay
    const [currentSessionId] = useAtom(currentSessionIdAtom);
    const [sessions] = useAtom(sessionsAtom);
    const [, updateSession] = useAtom(updateSessionAtom);
    const [, createSession] = useAtom(createSessionAtom);
    const [, setActiveSession] = useAtom(setActiveSessionAtom);
    const [lang] = useAtom(languageAtom);
    const t = useT();
    const [authUser] = useAtom(authUserAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    const [messagesMap, setMessagesMap] = useAtom(messagesMapAtom);
    const [novelSettings] = useAtom(novelChatSettingsAtom);
    // Build globals payload for server from novel settings
    const buildGlobals = React.useCallback(() => {
        const myName = ((novelSettings?.myName ?? '').trim()) || 'user';
        const myPersona = ((novelSettings?.myPersona ?? '').trim()) || '';
        const g = {
            user: myName,
            user_title: myName,
            persona_description: myPersona,
        };
        // Attach conversation settings into globals as well
        if (novelSettings?.language)
            g.language = novelSettings.language;
        if (novelSettings?.writingStyle)
            g.style = novelSettings.writingStyle;
        if (novelSettings?.person)
            g.person = novelSettings.person;
        if (Number.isFinite(Number(novelSettings?.fontSize)))
            g.font_size = Number(novelSettings?.fontSize);
        if (novelSettings?.customStyleEnabled) {
            const txt = String(novelSettings?.customStyleText || '').trim();
            if (txt)
                g.write_style = txt;
        }
        // keep deprecated fields if present (no harm on server side)
        if (novelSettings?.plotSpeed)
            g.plot_speed = novelSettings.plotSpeed;
        if ((novelSettings?.bannedWords || '').trim())
            g.banned_words = novelSettings?.bannedWords;
        return g;
    }, [novelSettings]);
    // Remove chain-of-thought style tags from raw text
    const stripThinkTags = React.useCallback((s) => {
        try {
            let t = String(s || '');
            t = t.replace(/<\s*(think|thinking)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
            t = t.replace(/<\s*(think|thinking)\b[^>]*\/>/gi, '');
            return t;
        }
        catch {
            return s;
        }
    }, []);
    // Pending buffer for unmatched code fences or trailing backticks across chunks
    const pendingRef = useRef('');
    // Removed: legacy UI streaming block markers (e.g., <<<HTML>>> or <<<UI>>>)
    // Hold pending UI info (adventure_info) until a session exists
    const pendingUiRef = useRef(null);
    // Removed: shortcuts quick-action UI and related protocol
    // Helper: try to parse "JSON-like" (tolerate comments & trailing commas)
    const parseJsonLike = React.useCallback((input) => {
        // 1) strict JSON
        try {
            return JSON.parse(input);
        }
        catch { }
        // 2) relaxed: strip comments + trailing commas
        try {
            let s = String(input || '');
            s = s.replace(/\/\*[\s\S]*?\*\//g, ''); // block comments
            s = s.replace(/(^|\s)\/\/.*$/gm, '$1'); // line comments
            s = s.replace(/,\s*(\]|\})/g, '$1'); // trailing commas
            try {
                return JSON.parse(s);
            }
            catch { }
            // 3) JSON5-ish: quote bare keys and convert single quotes to double quotes
            let s2 = s;
            // quote unquoted object keys (simple identifiers)
            s2 = s2.replace(/([\{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":');
            // replace single-quoted strings with double-quoted (naive but effective for typical payloads)
            s2 = s2.replace(/'((?:\\.|[^'\\])*)'/g, (_m, inner) => '"' + String(inner).replace(/"/g, '\\"') + '"');
            return JSON.parse(s2);
        }
        catch { }
        return undefined;
    }, []);
    // Helper: process fenced JSON blocks and HTML pages in plain text (outside UI blocks)
    const parseFences = React.useCallback((input) => {
        let buf = (pendingRef.current || '') + (input || '');
        pendingRef.current = '';
        const payloads = [];
        let out = '';
        let i = 0;
        while (true) {
            const start = buf.indexOf('```', i);
            if (start === -1) {
                out += buf.slice(i);
                break;
            }
            out += buf.slice(i, start);
            const end = buf.indexOf('```', start + 3);
            if (end === -1) {
                pendingRef.current = buf.slice(start);
                break;
            }
            const inside = buf.slice(start + 3, end);
            const m = inside.match(/^\s*([a-zA-Z0-9_-]+)?\s*([\s\S]*)$/);
            const lang = ((m?.[1]) || '').toLowerCase();
            const code = (m?.[2]) ?? inside;
            const codeTrim = String(code).trim();
            if (lang === 'json' || codeTrim.startsWith('{') || codeTrim.startsWith('[')) {
                const parsed = parseJsonLike(codeTrim);
                if (parsed !== undefined) {
                    try {
                        console.log('[UI] fenced JSON captured', { len: codeTrim.length, sample: codeTrim.slice(0, 80) });
                    }
                    catch { }
                    payloads.push(parsed);
                }
                else {
                    // Suppress JSON-ish fenced content from chat output to avoid noise; overlay will update on later chunks
                    try {
                        console.warn('[UI] fenced JSON parse failed; suppressed from chat', { sample: codeTrim.slice(0, 80) });
                    }
                    catch { }
                    // do not append to out
                }
            }
            else if (lang === 'html' || /<\s*(html|head|body)\b/i.test(codeTrim)) {
                // Capture HTML page fragment for pager overlay; suppress from chat output
                try {
                    console.log('[UI] fenced HTML captured', { len: codeTrim.length });
                }
                catch { }
                payloads.push({ ui_html_page: codeTrim });
            }
            else {
                out += buf.slice(start, end + 3);
            }
            i = end + 3;
            if (i >= buf.length)
                break;
        }
        return { out, payloads };
    }, [parseJsonLike]);
    const composeRulesRef = useRef(null);
    // Load persisted render_regex rules (if any) for current session, fall back to global.
    const loadPersistedRenderRules = React.useCallback((sid) => {
        try {
            const key = sid ? `render_regex_rules:${sid}` : 'render_regex_rules:global';
            const raw = localStorage.getItem(key);
            if (!raw)
                return false;
            const list = JSON.parse(raw);
            if (!Array.isArray(list))
                return false;
            const rules = [];
            for (const it of list) {
                const patRaw = String(it?.pattern || '').trim();
                const repl = typeof it?.replace === 'string' ? it.replace : '';
                const order = typeof it?.order === 'number' ? it.order : (parseInt(String(it?.order), 10) || 0);
                const markdownOnly = Boolean(it?.markdownOnly);
                const trimStrings = Array.isArray(it?.trimStrings) ? it.trimStrings.filter(Boolean) : [];
                if (!patRaw)
                    continue;
                let body = patRaw;
                let flags = 'g';
                if (patRaw.startsWith('/')) {
                    const last = patRaw.lastIndexOf('/');
                    if (last > 0) {
                        body = patRaw.slice(1, last);
                        const f = patRaw.slice(last + 1).replace(/[^gimsuy]/g, '');
                        flags = f.includes('g') ? f : (f + 'g');
                    }
                }
                try {
                    const rx = new RegExp(body, flags);
                    rules.push({ id: it?.id, order, regex: rx, replace: repl, markdownOnly, trimStrings });
                }
                catch { }
            }
            rules.sort((a, b) => (a.order || 0) - (b.order || 0));
            composeRulesRef.current = rules;
            setComposeVersion((v) => v + 1);
            return true;
        }
        catch {
            return false;
        }
    }, []);
    const hasHtml = React.useCallback((s) => /<([a-zA-Z][\w:-]*)\b[^>]*>/i.test(s), []);
    const stripTags = React.useCallback((s) => s.replace(/<[^>]*>/g, ''), []);
    const applyCompose = React.useCallback((text) => {
        let t = text ?? '';
        // Strip HTML comments like <!-- Statements Rejected --> from display
        try {
            t = t.replace(/<!--[\s\S]*?-->/g, '');
        }
        catch { }
        const pages = [];
        // During streaming: do not apply render rules or HTML extraction; only basic sanitation
        return { text: t, pages };
    }, [hasHtml, stripTags]);
    // Apply server-provided render regex (compose_meta) with placement support.
    // placement: 2=正文（非 thinking 内），6=thinking 标签内；缺省表示两处都应用。
    const applyRenderRegex = React.useCallback((text) => {
        const rulesAll = (composeRulesRef.current || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const rulesBody = rulesAll.filter(r => !Array.isArray(r.placements) || r.placements?.includes(2));
        const rulesThink = rulesAll.filter(r => !Array.isArray(r.placements) || r.placements?.includes(6));
        const applyList = (input, list) => {
            let out = String(input || '');
            for (const r of list) {
                if (!r?.regex)
                    continue;
                try {
                    const trims = Array.isArray(r.trimStrings) ? r.trimStrings : [];
                    const filter = (s) => {
                        let v = String(s ?? '');
                        for (const t of trims) {
                            if (t)
                                v = v.split(String(t)).join('');
                        }
                        return v;
                    };
                    const tpl = String(r.replace ?? '');
                    out = out.replace(r.regex, (full, ...args) => {
                        const named = (typeof args[args.length - 1] === 'object') ? (args[args.length - 1] || {}) : {};
                        let rep = tpl;
                        rep = rep.replace(/\$<([^>]+)>/g, (_m, name) => filter(named?.[name] ?? ''));
                        rep = rep.replace(/\$(\d+)/g, (_m, n) => filter(args[parseInt(n, 10) - 1] ?? ''));
                        rep = rep.replace(/\{\{\s*match\s*\}\}/gi, full);
                        return rep;
                    });
                }
                catch { }
            }
            return out;
        };
        const applyPhases = (input, list) => {
            const plain = list.filter(r => !r.markdownOnly);
            const md = list.filter(r => !!r.markdownOnly);
            let t = applyList(input, plain);
            t = applyList(t, md);
            return t;
        };
        // Split text by thinking blocks
        const src = String(text || '');
        const segs = [];
        try {
            const reOpen = /<\s*(think|thinking)([^>]*)>/gi;
            let i = 0;
            let m;
            while ((m = reOpen.exec(src)) !== null) {
                const start = m.index;
                if (start > i)
                    segs.push({ type: 'text', inner: src.slice(i, start) });
                const tag = m[1];
                const openTag = m[0];
                const closeRe = new RegExp(`<\\s*\\/\\s*${tag}\\s*>`, 'i');
                const rest = src.slice(reOpen.lastIndex);
                const mc = closeRe.exec(rest);
                if (mc) {
                    const inner = rest.slice(0, mc.index);
                    const closeTag = mc[0];
                    segs.push({ type: 'think', open: openTag, inner, close: closeTag });
                    i = reOpen.lastIndex + mc.index + closeTag.length;
                    reOpen.lastIndex = i;
                }
                else {
                    segs.push({ type: 'text', inner: src.slice(start) });
                    i = src.length;
                    break;
                }
            }
            if (i < src.length)
                segs.push({ type: 'text', inner: src.slice(i) });
        }
        catch {
            segs.length = 0;
            segs.push({ type: 'text', inner: src });
        }
        const out = segs.map(seg => {
            if (seg.type === 'think') {
                const inner = applyPhases(seg.inner, rulesThink);
                return `${seg.open || ''}${inner}${seg.close || ''}`;
            }
            return applyPhases(seg.inner, rulesBody);
        }).join('');
        return out;
    }, []);
    const processDataChunk = React.useCallback((chunk, isFinal = false) => {
        try {
            console.log('[UI] processDataChunk', { len: (chunk || '').length, isFinal });
        }
        catch { }
        let buf = chunk || '';
        const payloads = [];
        // 1) fence parse: may emit JSON payloads and leave plain text for chat
        const { out: fencedOut, payloads: p2 } = parseFences(buf);
        if (p2.length)
            payloads.push(...p2);
        let displayOut = fencedOut;
        // 2) optional tail handling for unfinished backticks (avoid leaking partial "``")
        if (!isFinal) {
            if (!pendingRef.current) {
                const tailMatch = displayOut.match(/`{1,2}$/);
                if (tailMatch) {
                    pendingRef.current = tailMatch[0];
                    displayOut = displayOut.slice(0, displayOut.length - tailMatch[0].length);
                }
            }
        }
        else {
            if (pendingRef.current) {
                displayOut += pendingRef.current;
                pendingRef.current = '';
            }
        }
        // 3) apply compose regex replacements and extract html pages
        const composed = applyCompose(displayOut);
        displayOut = composed.text;
        if (composed.pages && composed.pages.length) {
            for (const h of composed.pages)
                payloads.push({ ui_html_page: h });
        }
        try {
            console.log('[UI] processDataChunk result', { displayLen: displayOut.length, payloads: payloads.length });
        }
        catch { }
        return { displayText: displayOut, payloads };
    }, [parseFences, applyCompose]);
    // Transform for display when rendering saved/history messages
    const getComposedForDisplay = React.useCallback((content, role) => {
        if (role === 'user')
            return { text: content, pages: [] };
        const { text, pages } = applyCompose(content || '');
        return { text: text || '', pages };
    }, [applyCompose]);
    const [composeVersion, setComposeVersion] = useState(0);
    const [renderReady, setRenderReady] = useState(false);
    const renderReadyTimerRef = useRef(null);
    // Gate initial render until render_regex rules are available (or timeout fallback)
    useEffect(() => {
        // If rules already present, ready immediately
        const hasRules = Array.isArray(composeRulesRef.current) && composeRulesRef.current.length > 0;
        if (hasRules) {
            setRenderReady(true);
            if (renderReadyTimerRef.current) {
                clearTimeout(renderReadyTimerRef.current);
                renderReadyTimerRef.current = null;
            }
            return;
        }
        // Otherwise, start a short timeout to avoid indefinite blank in rare cases
        setRenderReady(false);
        if (renderReadyTimerRef.current) {
            clearTimeout(renderReadyTimerRef.current);
            renderReadyTimerRef.current = null;
        }
        renderReadyTimerRef.current = setTimeout(() => setRenderReady(true), 2000);
        return () => { if (renderReadyTimerRef.current) {
            clearTimeout(renderReadyTimerRef.current);
            renderReadyTimerRef.current = null;
        } };
    }, [composeVersion]);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const startEdit = (msgId, content) => {
        if (isStreaming)
            return; // avoid editing while streaming
        setEditingId(msgId);
        setEditValue(content || '');
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };
    const saveEdit = () => {
        try {
            if (!currentId || !editingId)
                return;
            const list = messagesMap[currentId] || [];
            const idx = list.findIndex((mm) => mm.id === editingId);
            if (idx === -1) {
                cancelEdit();
                return;
            }
            const nextList = list.slice();
            const m = nextList[idx];
            // 保存为原文（可包含 <think>/<thinking>），显示内容重新进行渲染正则处理
            const processed = applyRenderRegex(editValue || '');
            nextList[idx] = { ...m, raw: editValue, content: processed, timestamp: Date.now() };
            setMessagesMap({ ...messagesMap, [currentId]: nextList });
            setComposeVersion((v) => v + 1);
            // 持久化到当前会话，避免刷新后丢失
            const sess = sessions.find((s) => s.id === currentSessionId);
            if (sess) {
                const gm = chatToGameMessages(nextList);
                const updated = {
                    ...sess,
                    messages: gm,
                    updatedAt: Date.now(),
                    lastPlayedAt: Date.now(),
                    stats: { ...sess.stats, totalMessages: gm.length },
                };
                updateSession(updated);
            }
        }
        finally {
            cancelEdit();
        }
    };
    // Open a simple editor modal for repair result
    const openRepairEditor = React.useCallback((initial) => {
        return new Promise((resolve) => {
            try {
                const { modals } = require('@mantine/modals');
                let val = String(initial || '');
                modals.open({
                    title: '修复结果',
                    centered: true,
                    size: 'lg',
                    children: (_jsxs(Stack, { gap: "sm", children: [_jsx(Textarea, { autosize: true, minRows: 6, value: val, onChange: (e) => { val = e.currentTarget.value; } }), _jsxs(Group, { justify: "flex-end", children: [_jsx(Button, { variant: "subtle", onClick: () => { try {
                                            modals.closeAll();
                                        }
                                        catch { } ; resolve(null); }, children: "\u53D6\u6D88" }), _jsx(Button, { onClick: () => { try {
                                            modals.closeAll();
                                        }
                                        catch { } ; resolve(val); }, children: "\u786E\u5B9A" })] })] }))
                });
            }
            catch {
                resolve(null);
            }
        });
    }, []);
    const handleRepairAt = async (index, kind) => {
        if (isStreaming)
            return;
        try {
            const id = currentChat?.id || currentId;
            if (!id)
                return;
            const list = messagesMap[id] || [];
            if (index < 0 || index >= list.length)
                return;
            const target = list[index];
            if (!target || target.role === 'user')
                return;
            const targetText = stripThinkTags((target.raw || target.content || '')).trim();
            if (!targetText)
                return;
            const token = getStoredAuthToken();
            if (!token) {
                setAuthModalOpen(true);
                return;
            }
            const base = getApiBase();
            const body = { type: kind, text: targetText };
            const res = await httpFetch(`${base}/api/text/repair`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
                timeoutMs: 20000,
            });
            let repaired = '';
            try {
                const json = await res.json();
                repaired = String(json?.text ?? json?.data ?? '');
            }
            catch { }
            if (!repaired) {
                try {
                    repaired = await res.text();
                }
                catch { }
            }
            repaired = String(repaired || '').trim();
            const edited = await openRepairEditor(repaired || targetText);
            if (edited == null)
                return; // cancelled
            // Overwrite message with edited result: raw = edited (no think), content = applyRenderRegex(displayText)
            const display = applyRenderRegex(edited);
            const next = list.slice();
            next[index] = { ...target, raw: edited, content: display, timestamp: Date.now() };
            setMessagesMap({ ...messagesMap, [id]: next });
            setComposeVersion((v) => v + 1);
            // Optional: sync session meta
            syncSessionFromChat();
        }
        catch (e) {
            try {
                notifications.show({ color: 'red', title: '修复失败', message: String(e?.message || 'Server error'), withCloseButton: true });
            }
            catch { }
        }
    };
    const openRepairMenu = (index) => {
        try {
            const { modals } = require('@mantine/modals');
            modals.open({
                title: '选择修复类型',
                centered: true,
                withCloseButton: true,
                zIndex: 12000,
                children: (_jsxs(Stack, { gap: "sm", p: "xs", children: [_jsx(Button, { size: "md", radius: "md", style: { padding: '12px', fontSize: 16 }, onClick: () => { try {
                                modals.closeAll();
                            }
                            catch { } ; handleRepairAt(index, 'logic'); }, children: t('fix.logic') || '逻辑修复' }), _jsx(Button, { size: "md", radius: "md", variant: "light", style: { padding: '12px', fontSize: 16 }, onClick: () => { try {
                                modals.closeAll();
                            }
                            catch { } ; handleRepairAt(index, 'punct'); }, children: t('fix.punctuation') || '标点修复' }), _jsx(Button, { size: "md", radius: "md", variant: "light", style: { padding: '12px', fontSize: 16 }, onClick: () => { try {
                                modals.closeAll();
                            }
                            catch { } ; handleRepairAt(index, 'cot'); }, children: t('fix.cot') || '修复思维链' })] }))
            });
        }
        catch { }
    };
    // Memoize composed results for rendering to avoid recomputing on each keystroke
    const composedRender = React.useMemo(() => {
        const out = [];
        const len = messages.length;
        for (let i = 0; i < len; i++) {
            const m = messages[i];
            const isUser = m.role === 'user';
            const isLast = i === len - 1;
            const streamingForThis = !isUser && isLast && isStreaming;
            if (isUser || streamingForThis) {
                out.push({ id: m.id, text: m.content, pagesCount: 0 });
                continue;
            }
            const res = applyCompose(m.content || '');
            const textWithRegex = applyRenderRegex(res.text || '');
            out.push({ id: m.id, text: textWithRegex, pagesCount: (res.pages || []).length });
        }
        return out;
    }, [messages, isStreaming, composeVersion, applyCompose, applyRenderRegex]);
    // Hydrate overlay pages from existing history when overlay mounts or rules change
    const hydratedOnceRef = useRef(false);
    const hydrateOverlayFromHistory = React.useCallback((doReset) => {
        if (!onUiData)
            return;
        try {
            if (doReset)
                onUiData({ ui_html_reset: true });
            const pages = [];
            for (const m of messages || []) {
                if (!m)
                    continue;
                if (m.role && m.role !== 'user') {
                    const res = applyCompose(m.content || '');
                    if (res.pages && res.pages.length)
                        pages.push(...res.pages);
                }
            }
            if (pages.length)
                onUiData({ ui_pages: pages });
            hydratedOnceRef.current = true;
        }
        catch { }
    }, [messages, onUiData, applyCompose]);
    // On first mount (overlay opened), hydrate once from history
    useEffect(() => {
        if (!hydratedOnceRef.current)
            hydrateOverlayFromHistory(true);
    }, [hydrateOverlayFromHistory]);
    // On mount / session change: try to restore render rules from localStorage so history also applies them
    useEffect(() => {
        if (composeRulesRef.current && composeRulesRef.current.length > 0)
            return;
        const ok = loadPersistedRenderRules(currentSessionId);
        if (!ok)
            loadPersistedRenderRules(null);
    }, [currentSessionId, loadPersistedRenderRules]);
    const applyUiPayloads = React.useCallback((payloads) => {
        if (!payloads || !payloads.length)
            return;
        // forward to overlay
        try {
            console.log('[UI] applyUiPayloads forwarding', { count: payloads.length });
            payloads.forEach((p, i) => {
                try {
                    console.log('[UI] -> payload', i, typeof p, Array.isArray(p) ? 'array' : (p && typeof p === 'object' ? Object.keys(p) : 'primitive'));
                }
                catch { }
                onUiData?.(p);
            });
        }
        catch { }
        // persist uiState to current session (shallow merge)
        try {
            if (!currentSessionId)
                return;
            const sess = sessions.find((s) => s.id === currentSessionId);
            if (!sess)
                return;
            // Ignore html pager payloads for persisted uiState
            const filtered = payloads.filter((p) => !p || typeof p !== 'object' || (!('ui_html_page' in p) && !('ui_html_reset' in p) && !('ui_pages' in p)));
            if (filtered.length > 0) {
                let nextState = sess.uiState || {};
                for (const p of filtered) {
                    if (p && typeof p === 'object' && !Array.isArray(p))
                        nextState = { ...nextState, ...p };
                    else
                        nextState = p;
                }
                console.log('[UI] uiState merged', { keys: Object.keys(nextState || {}) });
                updateSession({ ...sess, uiState: nextState });
            }
        }
        catch { }
    }, [currentSessionId, sessions, updateSession, onUiData]);
    const syncSessionFromChat = React.useCallback(() => {
        if (!currentSessionId)
            return;
        const sess = sessions.find((s) => s.id === currentSessionId);
        if (!sess)
            return;
        const gm = chatToGameMessages(messages);
        const updated = {
            ...sess,
            messages: gm,
            updatedAt: Date.now(),
            lastPlayedAt: Date.now(),
            stats: { ...sess.stats, totalMessages: gm.length },
        };
        updateSession(updated);
    }, [currentSessionId, sessions, messages, updateSession]);
    // Force re-render when compose rules change so history re-applies replacements
    // Attach WS handler; connection is created by clicking a card (wsManager)
    useEffect(() => {
        const handler = (action) => {
            // try { console.log('[WS] action', action?.type, action); } catch {}
            // Bind or create session by server session_id if present
            const sid = action?.data?.session_id || action?.session_id;
            if (sid) {
                try {
                    wsManager.setSessionId(sid);
                }
                catch { }
                const byServer = sessions.find((s) => s.serverSessionId === sid);
                if (byServer) {
                    if (currentSessionId !== byServer.id) {
                        setActiveSession(byServer.id);
                    }
                }
                else {
                    if (currentSessionId) {
                        const cur = sessions.find((s) => s.id === currentSessionId);
                        if (cur && !cur.serverSessionId) {
                            updateSession({ ...cur, serverSessionId: sid });
                        }
                    }
                    else if (activeScriptId) {
                        const newSess = createSessionForScript(activeScriptId, (currentChat?.title || 'Chat'));
                        newSess.serverSessionId = sid;
                        createSession(newSess);
                        setActiveSession(newSess.id);
                    }
                }
            }
            if (!sessionReady)
                setSessionReady(true);
            // Server error handling -> show notification at top
            if (action.type === 'server_error' || action.type === 'error') {
                const code = action?.data?.error?.code || '';
                const msg = action?.data?.error?.message || action?.data?.message || 'Server error';
                try {
                    notifications.show({ color: 'red', title: code || 'Error', message: String(msg), withCloseButton: true, autoClose: false });
                }
                catch { }
                return;
            }
            if (action.type === 'session_create') {
                const sid = action.data && (action.data.session_id || action.data.id);
                if (sid) {
                    try {
                        wsManager.setSessionId(sid);
                    }
                    catch { }
                    if (currentSessionId) {
                        const sess = sessions.find((s) => s.id === currentSessionId);
                        if (sess && sess.serverSessionId !== sid) {
                            updateSession({ ...sess, serverSessionId: sid });
                        }
                        // If we had pending UI info before the session existed, apply it now
                        if (pendingUiRef.current) {
                            const { ui, img, gtype } = pendingUiRef.current;
                            const patch = { ...sess };
                            if (ui && ui !== sess?.scriptUiCode)
                                patch.scriptUiCode = ui;
                            if (img && img !== sess?.scriptImageUrl)
                                patch.scriptImageUrl = img;
                            if (gtype)
                                patch.uiState = { ...(sess?.uiState || {}), game_type: gtype };
                            if (patch && sess && (patch.scriptUiCode !== sess.scriptUiCode || patch.scriptImageUrl !== sess.scriptImageUrl || (gtype && ((sess.uiState || {}).game_type !== gtype)))) {
                                updateSession(patch);
                            }
                            pendingUiRef.current = null;
                        }
                    }
                }
                // hydrate UI code if provided by server
                try {
                    const ui = action?.data?.ui_code;
                    const img = action?.data?.image_url;
                    if (currentSessionId && (ui || img)) {
                        const sess = sessions.find((s) => s.id === currentSessionId);
                        if (sess) {
                            const patch = { ...sess };
                            if (typeof ui === 'string' && ui !== sess.scriptUiCode)
                                patch.scriptUiCode = ui;
                            if (typeof img === 'string' && img !== sess.scriptImageUrl)
                                patch.scriptImageUrl = img;
                            if (patch !== sess)
                                updateSession(patch);
                        }
                    }
                }
                catch { }
                // reset compose rules for new session context
                composeRulesRef.current = null;
                return;
            }
            if (action.type === 'compose_meta') {
                try {
                    const meta = action?.data;
                    const src = (meta && (meta.render_regex || meta.renderRegex));
                    let list = [];
                    if (Array.isArray(src))
                        list = src;
                    else if (src && typeof src === 'object') {
                        // If provided as object map, collect values
                        list = Object.values(src);
                    }
                    const rules = [];
                    const persisted = [];
                    for (const it of list) {
                        const patRaw = String(it?.pattern || '').trim();
                        const repl = typeof it?.replace === 'string' ? it.replace : '';
                        const order = typeof it?.order === 'number' ? it.order : (parseInt(String(it?.order), 10) || 0);
                        const markdownOnly = Boolean(it?.markdownOnly);
                        const trimStrings = Array.isArray(it?.trimStrings) ? it.trimStrings.filter(Boolean) : [];
                        const placements = Array.isArray(it?.placement || it?.placements)
                            ? (it?.placement || it?.placements).map((x) => parseInt(String(x), 10)).filter((n) => Number.isFinite(n))
                            : undefined;
                        if (!patRaw)
                            continue;
                        let body = patRaw;
                        let flags = 'g';
                        if (patRaw.startsWith('/')) {
                            const last = patRaw.lastIndexOf('/');
                            if (last > 0) {
                                body = patRaw.slice(1, last);
                                const f = patRaw.slice(last + 1).replace(/[^gimsuy]/g, '');
                                flags = f.includes('g') ? f : (f + 'g');
                            }
                        }
                        try {
                            const rx = new RegExp(body, flags);
                            rules.push({ id: it?.id, order, regex: rx, replace: repl, markdownOnly, trimStrings, placements });
                            persisted.push({ id: it?.id, order, pattern: patRaw, replace: repl, markdownOnly, trimStrings, placements });
                        }
                        catch (e) {
                            try {
                                console.warn('[compose_meta] invalid regex', it?.pattern, e);
                            }
                            catch { }
                        }
                    }
                    rules.sort((a, b) => (a.order || 0) - (b.order || 0));
                    composeRulesRef.current = rules;
                    try {
                        console.log('[compose_meta] rules installed', rules.length);
                    }
                    catch { }
                    // Persist for refresh
                    try {
                        const key = currentSessionId ? `render_regex_rules:${currentSessionId}` : 'render_regex_rules:global';
                        localStorage.setItem(key, JSON.stringify(persisted));
                    }
                    catch { }
                    // After installing rules, rehydrate overlay from existing history
                    hydrateOverlayFromHistory(true);
                    setComposeVersion((v) => v + 1);
                    setRenderReady(true);
                }
                catch (e) {
                    try {
                        console.warn('[compose_meta] parse error', e);
                    }
                    catch { }
                }
                return;
            }
            // Removed: shortcuts and sub_actions protocol handling
            if (action.type === 'character_response_stream') {
                const { text = '', is_streaming } = action.data || {};
                try {
                    console.log('[WS] stream chunk', { len: (text || '').length, is_streaming });
                }
                catch { }
                // Accumulate raw chunks for final compose
                if (typeof text === 'string' && text.length) {
                    streamBufferRef.current += text;
                    // Interim: append raw for UI; no replacements, no overlay updates
                    appendAssistantChunk(text);
                }
                setIsStreaming(Boolean(is_streaming));
                if (is_streaming === false) {
                    // Finalize once with full text
                    const full = streamBufferRef.current || '';
                    streamBufferRef.current = '';
                    // Reset any partial fence tail and overlay pages
                    pendingRef.current = '';
                    const { displayText } = processDataChunk(full, true);
                    const finalText = applyRenderRegex(displayText);
                    // Overwrite last assistant message with final processed text
                    try {
                        const chatId = (currentChat?.id || currentId);
                        if (chatId) {
                            const list = messagesMap[chatId] || [];
                            if (list.length) {
                                const idx = list.length - 1;
                                const last = list[idx];
                                if (last && last.role !== 'user') {
                                    const next = list.slice();
                                    next[idx] = { ...last, raw: stripThinkTags(full), content: finalText, timestamp: Date.now() };
                                    setMessagesMap({ ...messagesMap, [chatId]: next });
                                }
                            }
                        }
                    }
                    catch { }
                    // Session sync after finalization
                    syncSessionFromChat();
                    // Reset guard
                    uiPagerResetRef.current = false;
                }
                return;
            }
            if (action.type === 'character_response') {
                const { text = '' } = action.data || {};
                try {
                    console.log('[WS] non-stream response', { len: (text || '').length });
                }
                catch { }
                if (typeof text === 'string' && text.length) {
                    const { displayText } = processDataChunk(text, true);
                    const finalText = applyRenderRegex(displayText);
                    // Append as a new assistant message with raw + processed content
                    try {
                        const id = currentChat?.id || currentId;
                        if (id) {
                            const list = messagesMap[id] || [];
                            const next = list.slice();
                            next.push({ id: `a${Date.now()}`, role: 'assistant', raw: stripThinkTags(text), content: finalText, timestamp: Date.now() });
                            setMessagesMap({ ...messagesMap, [id]: next });
                        }
                        else {
                            appendAssistantChunk(finalText);
                        }
                    }
                    catch { }
                }
                syncSessionFromChat();
                setIsStreaming(false);
                return;
            }
            if (action.type === 'assistant_message') {
                const text = (action.data && (action.data.text || action.data.message)) || '';
                if (typeof text === 'string' && text.length) {
                    const { displayText } = processDataChunk(text, true);
                    const finalText = applyRenderRegex(displayText);
                    try {
                        const id = currentChat?.id || currentId;
                        if (id) {
                            const list = messagesMap[id] || [];
                            const next = list.slice();
                            next.push({ id: `a${Date.now()}`, role: 'assistant', raw: stripThinkTags(text), content: finalText, timestamp: Date.now() });
                            setMessagesMap({ ...messagesMap, [id]: next });
                        }
                        else {
                            appendAssistantChunk(finalText);
                        }
                    }
                    catch { }
                }
                // 非流式，完整消息后同步
                syncSessionFromChat();
                setIsStreaming(false);
                return;
            }
            if (action.type === 'session_ready' || action.type === 'session_started') {
                // keep compose rules; server may send new compose_meta later and overwrite
                // hydrate UI code if provided by server
                try {
                    const ui = action?.data?.ui_code;
                    const img = action?.data?.image_url;
                    if (currentSessionId && (ui || img)) {
                        const sess = sessions.find((s) => s.id === currentSessionId);
                        if (sess) {
                            const patch = { ...sess };
                            if (typeof ui === 'string' && ui !== sess.scriptUiCode)
                                patch.scriptUiCode = ui;
                            if (typeof img === 'string' && img !== sess.scriptImageUrl)
                                patch.scriptImageUrl = img;
                            if (patch !== sess)
                                updateSession(patch);
                        }
                    }
                }
                catch { }
                setSessionReady(true);
                syncSessionFromChat();
                return;
            }
            if (action.type === 'adventure_info') {
                try {
                    console.log('[UI] adventure_info received', { hasUi: !!action?.data?.ui_code, uiLen: (action?.data?.ui_code || '').length, hasImg: !!action?.data?.image_url });
                }
                catch { }
                try {
                    const ui = action?.data?.ui_code;
                    const img = action?.data?.image_url;
                    const gtype = action?.data?.game_type;
                    const sid = action?.data?.session_id || action?.session_id;
                    if (ui || img || gtype) {
                        // Prefer current session; else map by server session id; else fallback to first session with active script id
                        let sess = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
                        if (!sess && sid)
                            sess = sessions.find((s) => s.serverSessionId === sid);
                        if (!sess && activeScriptId)
                            sess = sessions.find((s) => s.scriptId === activeScriptId);
                        if (sess) {
                            const patch = { ...sess };
                            if (typeof ui === 'string' && ui && ui !== sess.scriptUiCode)
                                patch.scriptUiCode = ui;
                            if (typeof img === 'string' && img && img !== sess.scriptImageUrl)
                                patch.scriptImageUrl = img;
                            if (gtype)
                                patch.uiState = { ...(sess.uiState || {}), game_type: gtype };
                            const changed = patch.scriptUiCode !== sess.scriptUiCode || patch.scriptImageUrl !== sess.scriptImageUrl || (gtype && ((sess.uiState || {}).game_type !== gtype));
                            try {
                                console.log('[UI] updating session with UI/IMG (adventure_info)', { changed });
                            }
                            catch { }
                            if (changed)
                                updateSession(patch);
                        }
                        else {
                            // No session yet: stash and apply when session gets created
                            pendingUiRef.current = { ui: typeof ui === 'string' ? ui : undefined, img: typeof img === 'string' ? img : undefined, gtype };
                            try {
                                console.warn('[UI] adventure_info: stashed pending UI until session is ready');
                            }
                            catch { }
                        }
                    }
                    // If prologue provided and this is a new chat (no local messages yet), inject it as the first assistant message
                    const prologue = String(action?.data?.prologue || '').trim();
                    if (prologue) {
                        try {
                            const chatId = currentId;
                            if (chatId && !prologueInsertedRef.current[chatId] && (messages?.length || 0) === 0) {
                                prologueInsertedRef.current[chatId] = true;
                                // Process prologue like a final assistant message: apply fences + render regex
                                const { displayText } = processDataChunk(prologue, true);
                                const finalText = applyRenderRegex(displayText);
                                appendAssistantChunk(finalText);
                                syncSessionFromChat();
                            }
                        }
                        catch { }
                    }
                }
                catch { }
                return;
            }
        };
        wsManager.setHandler(handler);
        return () => {
            // Detach handler when unmounting
            wsManager.setHandler(null);
        };
    }, [appendAssistantChunk, sessionReady, syncSessionFromChat]);
    // Reset session state when switching scripts
    useEffect(() => {
        setSessionReady(false);
        setIsStreaming(false);
    }, [activeScriptId]);
    // No local mode: sessionReady is set by server events
    // Keep WS language in sync when user switches languages
    useEffect(() => {
        try {
            wsManager.setLanguage(lang);
        }
        catch { }
    }, [lang]);
    // Hide loading indicator if WS connection closes (e.g., network断开)
    useEffect(() => {
        if (typeof wsManager.onStatus !== 'function')
            return;
        const unsub = wsManager.onStatus((s) => {
            if (s && s.state === 'closed')
                setIsStreaming(false);
        });
        return () => { try {
            unsub && unsub();
        }
        catch { } };
    }, []);
    // streaming content updates are handled via appendAssistantChunk atom
    const prevMsgCountRef = useRef(0);
    const uiPagerResetRef = useRef(false);
    const streamBufferRef = useRef('');
    const prologueInsertedRef = useRef({});
    const stickToBottom = React.useCallback((force = false) => {
        const el = scrollRef.current;
        if (!el)
            return;
        if (force) {
            el.scrollTop = el.scrollHeight;
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = el;
        const distance = scrollHeight - (scrollTop + clientHeight);
        const nearBottom = distance < 120; // threshold
        if (nearBottom)
            el.scrollTop = scrollHeight;
    }, []);
    // Scrolling policy:
    // - When user sends a message: scroll to bottom.
    // - When server/assistant message arrives (new item): scroll to the start of that message.
    // - While streaming assistant chunks: do not auto-scroll.
    useEffect(() => {
        const prevCount = prevMsgCountRef.current || 0;
        const curCount = messages?.length || 0;
        const added = curCount > prevCount;
        prevMsgCountRef.current = curCount;
        if (!added)
            return; // no new message item (likely just streaming update)
        const last = messages && messages[messages.length - 1];
        if (!last)
            return;
        if (last.role === 'user') {
            // User input: jump to bottom so input/result context is visible
            stickToBottom(true);
        }
        else {
            // Assistant/system/narrator: align viewport to the start of the new message
            const el = lastMsgRef.current;
            if (el && typeof el.scrollIntoView === 'function') {
                try {
                    el.scrollIntoView({ block: 'start' });
                }
                catch { /* fallback below */ }
            }
            else {
                // Fallback: ensure we are not stuck at bottom
                stickToBottom(false);
            }
        }
    }, [messages, stickToBottom]);
    // no-op
    useEffect(() => {
        if (!currentId)
            createChat('New Chat');
    }, [currentId]);
    const isCommentOnly = (s) => /^\s*<!--[\s\S]*?-->\s*$/.test(s || '');
    const stripComment = (s) => String(s || '').replace(/^\s*<!--/, '').replace(/-->\s*$/, '');
    const buildServerHistory = (list, nextUser) => {
        const out = [];
        for (let i = 0; i < list.length; i++) {
            const m = list[i];
            if (typeof m?.content === 'string' && isCommentOnly(m.content)) {
                // Treat comment-only as the authoritative assistant content; skip the immediate visible assistant that follows
                out.push({ role: 'assistant', content: stripComment(m.content) });
                // Skip subsequent assistant visible messages that belong to the same reply (until next user message)
                let j = i + 1;
                while (j < list.length && list[j].role !== 'user')
                    j++;
                i = j - 1;
                continue;
            }
            const role = m.role === 'user' ? 'user' : 'assistant';
            const body0 = (typeof m.raw === 'string' && m.raw != null) ? m.raw : (m.content || '');
            out.push({ role, content: stripThinkTags(body0) });
        }
        if (nextUser && nextUser.trim())
            out.push({ role: 'user', content: stripThinkTags(nextUser.trim()) });
        return out;
    };
    const sendTextNow = (wireText, displayOverride) => {
        const content = wireText.trim();
        if (!content)
            return;
        if (isStreaming)
            return; // block sending while server is streaming
        // Ensure there is an active chat to receive the message
        if (!currentId) {
            createChat(currentChat?.title || 'New Chat', activeScriptId);
            // Defer actual send until chat id is set
            pendingFirstTextRef.current = { wire: content, display: displayOverride };
            return;
        }
        // Build history snapshot BEFORE appending (so we can push the wireText as last item)
        const historyForServer = buildServerHistory(messages, content);
        // Immediately append to UI (use display content for UI, but send wire content in history)
        const display = (displayOverride ?? content);
        send({ role: 'user', content: display, raw: stripThinkTags(content) });
        // Find current serverSessionId if any
        const curSess = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
        const serverSid = curSess?.serverSessionId;
        const finalScriptId = activeScriptId || curSess?.scriptId;
        try {
            // Lazy connect: connect on first send, then send text with full history
            if (!finalScriptId)
                throw new Error('No active script selected');
            const globals = buildGlobals();
            wsManager.sendTextLazy({ scriptId: finalScriptId, language: lang, sessionId: serverSid, text: content, history: historyForServer, globals });
            setIsStreaming(true);
        }
        catch (e) {
            console.error('WS send error', e);
        }
        syncSessionFromChat();
    };
    // Direct quick button from Markdown sends its label as text
    const sendQuickLabel = (text) => {
        const base = String(text || '').trim();
        if (!base)
            return;
        // Slash commands support for quick buttons
        if (base.includes('/send') || base.includes('/trigger')) {
            try {
                console.log('[triggerSlash] quickLabel input', base);
            }
            catch { }
            const cur = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
            const serverSid = cur?.serverSessionId;
            const finalScriptId = activeScriptId || cur?.scriptId;
            const deps = {
                appendToChatUI: (t) => { try {
                    console.log('[triggerSlash] quickLabel append', t);
                }
                catch { } ; send({ role: 'user', content: t, raw: stripThinkTags(t) }); },
                sendToServer: (t) => {
                    try {
                        console.log('[triggerSlash] quickLabel send', { t, finalScriptId, serverSid });
                    }
                    catch { }
                    if (!finalScriptId)
                        return;
                    const hist = buildServerHistory(messages, t);
                    const globals = buildGlobals();
                    wsManager.sendTextLazy({ scriptId: finalScriptId, language: lang, sessionId: serverSid, text: t, history: hist, globals });
                    setIsStreaming(true);
                },
            };
            const res = triggerSlash(base, deps);
            try {
                console.log('[triggerSlash] quickLabel result', res);
            }
            catch { }
            if (!res.handled)
                sendTextNow(base);
            return;
        }
        sendTextNow(base);
    };
    // Expose global triggerSlash for embedded HTML snippets to set input or send
    useEffect(() => {
        const handler = (input) => {
            const base = String(input || '').trim();
            const cur = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
            const serverSid = cur?.serverSessionId;
            const finalScriptId = activeScriptId || cur?.scriptId;
            const deps = {
                appendToChatUI: (t) => { send({ role: 'user', content: t, raw: stripThinkTags(t) }); },
                setInput: (t) => { setDraft(t); },
                sendToServer: (t) => {
                    if (!finalScriptId)
                        return;
                    const hist = buildServerHistory(messages, t);
                    const globals = buildGlobals();
                    wsManager.sendTextLazy({ scriptId: finalScriptId, language: lang, sessionId: serverSid, text: t, history: hist, globals });
                    setIsStreaming(true);
                },
            };
            try {
                triggerSlash(base, deps);
            }
            catch { }
        };
        window.triggerSlash = handler;
        return () => { try {
            delete window.triggerSlash;
        }
        catch { } };
        // We intentionally do not include volatile deps to keep a stable handler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeScriptId, currentSessionId]);
    // Flush pending first text once a chat id exists
    useEffect(() => {
        if (currentId && pendingFirstTextRef.current) {
            const pending = pendingFirstTextRef.current;
            pendingFirstTextRef.current = null;
            // Defer to next microtask so atoms reflect latest id
            Promise.resolve().then(() => sendTextNow(pending.wire, pending.display));
        }
    }, [currentId]);
    // Measure floating layer height and set bottom padding for scroll area
    useEffect(() => {
        const el = floatsRef.current;
        const scroll = scrollRef.current;
        if (!scroll)
            return;
        const h = el ? el.offsetHeight : 0;
        scroll.style.setProperty('--chat-bottom-pad', `${h + 12}px`);
        try {
            onFloatsHeightChange?.(h + 12);
        }
        catch { }
    }, [isStreaming, draft.length, onFloatsHeightChange]);
    const handleSend = () => {
        const content = draft.trim();
        if (!content)
            return;
        if (isStreaming)
            return; // block while streaming
        if (!authUser || !getStoredAuthToken()) {
            setAuthModalOpen(true);
            return;
        }
        setDraft('');
        sendTextNow(content);
        // ensure view sticks to bottom after sending
        setTimeout(() => stickToBottom(true), 0);
    };
    const sendServerAction = async (action) => {
        try {
            if (!authUser || !getStoredAuthToken()) {
                setAuthModalOpen(true);
                return;
            }
            // Determine current session/server id
            const cur = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
            const serverSid = cur?.serverSessionId;
            const finalScriptId = activeScriptId || cur?.scriptId;
            if (!finalScriptId)
                throw new Error('No script selected');
            await wsManager.sendActionLazy({ scriptId: finalScriptId, language: lang, sessionId: serverSid, action });
        }
        catch (e) {
            notifications.show({ color: 'red', title: t('error.title') || '错误', message: String(e?.message || '操作失败'), withCloseButton: true });
        }
    };
    const handleRegenerateAt = async (index) => {
        if (isStreaming)
            return;
        const ok = await confirmDialog({
            title: t('confirm.title') || '确认',
            message: t('confirm.regenerate') || '确定重新生成该条消息？这会删除上次回复并重新请求。',
            labels: { confirm: t('confirm.ok') || '确定', cancel: t('confirm.cancel') || '取消' },
        });
        if (!ok)
            return;
        try {
            // 1) Remove the last assistant message locally (index is guaranteed to be the last and role=assistant by UI)
            const id = currentChat?.id || currentId;
            if (!id)
                return;
            const list = messagesMap[id] || [];
            if (index < 0 || index >= list.length)
                return;
            const target = list[index];
            if (!target || target.role !== 'assistant' || index !== list.length - 1)
                return;
            // Find the last user message content to resubmit
            let j = index - 1;
            let lastUserText = null;
            while (j >= 0) {
                if (list[j].role === 'user') {
                    lastUserText = list[j].content;
                    break;
                }
                j--;
            }
            if (!lastUserText || !lastUserText.trim())
                return;
            // Remove the last assistant locally
            const nextList = list.slice(0, index);
            setMessagesMap({ ...messagesMap, [id]: nextList });
            // Sync to session history so local session metadata stays consistent
            if (currentSessionId) {
                const sess = sessions.find((s) => s.id === currentSessionId);
                if (sess) {
                    const gm = chatToGameMessages(nextList);
                    const updated = {
                        ...sess,
                        messages: gm,
                        updatedAt: Date.now(),
                        lastPlayedAt: Date.now(),
                        stats: { ...sess.stats, totalMessages: gm.length },
                    };
                    updateSession(updated);
                }
            }
            // 2) Resubmit the last user request without appending a new user message in UI
            const cur = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
            const serverSid = cur?.serverSessionId;
            const finalScriptId = activeScriptId || cur?.scriptId;
            if (!finalScriptId)
                return;
            // Build history snapshot without adding another user turn
            const history = buildServerHistory(nextList);
            setIsStreaming(true);
            const globals = buildGlobals();
            wsManager.sendTextLazy({ scriptId: finalScriptId, language: lang, sessionId: serverSid, text: lastUserText, history, globals });
        }
        catch (e) {
            console.error('Regenerate failed', e);
        }
    };
    const handleDeleteAt = async (index) => {
        if (isStreaming)
            return;
        const ok = await confirmDialog({
            title: t('confirm.title') || '确认',
            message: t('confirm.delete') || '确定删除该条消息？',
            labels: { confirm: t('confirm.ok') || '确定', cancel: t('confirm.cancel') || '取消' },
        });
        if (!ok)
            return;
        // Local-only delete: do not notify server; next send will include full history
        try {
            const id = currentChat?.id || currentId;
            if (!id)
                return;
            const list = messagesMap[id] || [];
            if (index < 0 || index >= list.length)
                return;
            const next = list.slice(0, index).concat(list.slice(index + 1));
            setMessagesMap({ ...messagesMap, [id]: next });
            // Also sync to session history so local session metadata stays consistent
            if (currentSessionId) {
                const sess = sessions.find((s) => s.id === currentSessionId);
                if (sess) {
                    const gm = chatToGameMessages(next);
                    const updated = {
                        ...sess,
                        messages: gm,
                        updatedAt: Date.now(),
                        lastPlayedAt: Date.now(),
                        stats: { ...sess.stats, totalMessages: gm.length },
                    };
                    updateSession(updated);
                }
            }
        }
        catch (e) {
            console.error('Local delete failed', e);
        }
    };
    const handleKeyDown = (e) => {
        if (isStreaming) {
            // prevent accidental sends while streaming
            if (e.key === 'Enter' && !e.shiftKey)
                e.preventDefault();
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    return (_jsxs(Stack, { h: "100%", gap: 0, style: { minHeight: 0, height: '100%', position: 'relative' }, children: [_jsx(Box, { className: "chat-scroll", style: { flex: 1, overflow: 'auto', minHeight: 0 }, ref: scrollRef, children: _jsxs(Stack, { p: "md", gap: "sm", children: [!renderReady && (_jsxs(Group, { justify: "center", gap: 6, style: { opacity: 0.8 }, children: [_jsx(Loader, { size: "sm" }), _jsx(Text, { size: "sm", c: "dimmed", children: "\u6B63\u5728\u51C6\u5907\u6E32\u67D3\u89C4\u5219\u2026" })] })), renderReady && (messages.map((m, idx) => {
                            const isHiddenComment = typeof m.content === 'string' && /^\s*<!--[\s\S]*?-->\s*$/.test(m.content);
                            if (isHiddenComment)
                                return null;
                            const isUser = m.role === 'user';
                            const isLast = idx === messages.length - 1;
                            const streamingForThis = !isUser && isLast && isStreaming;
                            const styleForUser = 'novel'; // force novel paper style
                            const userContainerStyle = (true)
                                ? { alignSelf: 'stretch', width: '100%', maxWidth: '100%', padding: '2px 0', position: 'relative' }
                                : { alignSelf: 'flex-end', maxWidth: '85%', padding: '2px 0' };
                            if (isUser) {
                                // 用户消息：在所有非 markdown 风格（含 novel 纸页）下也提供编辑/删除操作
                                if (messageStyle !== 'markdown') {
                                    return (_jsx("div", { style: userContainerStyle, children: editingId === m.id ? (_jsxs("div", { style: { marginTop: 8 }, children: [_jsx(Textarea, { autosize: true, minRows: 2, maxRows: 10, value: editValue, onChange: (e) => setEditValue(e.currentTarget.value) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "filled", onClick: saveEdit, title: t('save') || '保存', children: _jsx(IconSend, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "subtle", onClick: cancelEdit, title: t('cancel') || '取消', children: _jsx(IconX, { size: 14 }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(MarkdownMessage, { content: m.content, renderStyle: styleForUser, senderRole: 'user', timestamp: m.timestamp, readStatus: 'read', onButtonClick: (label) => sendQuickLabel(label) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => startEdit(m.id, (m.raw ?? m.content)), title: t('edit') || '编辑', disabled: isStreaming, children: _jsx(IconEdit, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => handleDeleteAt(idx), title: t('delete') || '删除', disabled: isStreaming, children: _jsx(IconTrash, { size: 14 }) })] })] })) }, m.id));
                                }
                                return (_jsx(Card, { withBorder: true, p: "md", radius: "md", className: `chat-message ${m.role}`, style: { alignSelf: 'flex-end', maxWidth: '80%', position: 'relative' }, children: editingId === m.id ? (_jsxs("div", { style: { marginTop: 8 }, children: [_jsx(Textarea, { autosize: true, minRows: 2, maxRows: 10, value: editValue, onChange: (e) => setEditValue(e.currentTarget.value) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "filled", onClick: saveEdit, title: t('save') || '保存', children: _jsx(IconSend, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "subtle", onClick: cancelEdit, title: t('cancel') || '取消', children: _jsx(IconX, { size: 14 }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(MarkdownMessage, { content: m.content, renderStyle: 'markdown', senderRole: 'user', timestamp: m.timestamp, readStatus: 'read', onButtonClick: (label) => sendQuickLabel(label) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => startEdit(m.id, (m.raw ?? m.content)), title: t('edit') || '编辑', disabled: isStreaming, children: _jsx(IconEdit, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", title: "\u4FEE\u590D", disabled: isStreaming, onClick: () => openRepairMenu(idx), children: _jsx(IconWand, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => handleDeleteAt(idx), title: t('delete') || '删除', disabled: isStreaming, children: _jsx(IconTrash, { size: 14 }) })] })] })) }, m.id));
                            }
                            // Assistant/System/Narrator
                            if (messageStyle !== 'markdown') {
                                const composed = (streamingForThis
                                    ? { text: m.content, pagesCount: 0 }
                                    : composedRender[idx] || { id: m.id, text: m.content, pagesCount: 0 });
                                return (_jsxs("div", { style: messageStyle === 'messager' ? { alignSelf: 'flex-start', maxWidth: '85%', padding: '2px 0', position: 'relative' } : { alignSelf: 'stretch', width: '100%', maxWidth: '100%', padding: '2px 0', position: 'relative' }, ref: isLast && !isUser ? (el) => { if (el)
                                        lastMsgRef.current = el; } : undefined, children: [composed.pagesCount > 0 && (_jsx("div", { style: { marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }, children: _jsxs("span", { style: { fontSize: 12, opacity: 0.85 }, children: ["\u754C\u9762\u7247\u6BB5 \u00B7 ", composed.pagesCount] }) })), editingId === m.id ? (_jsxs("div", { style: { marginTop: 8 }, children: [_jsx(Textarea, { autosize: true, minRows: 2, maxRows: 10, value: editValue, onChange: (e) => setEditValue(e.currentTarget.value) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "filled", onClick: saveEdit, title: t('save') || '保存', children: _jsx(IconSend, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "subtle", onClick: cancelEdit, title: t('cancel') || '取消', children: _jsx(IconX, { size: 14 }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(MarkdownMessage, { content: composed.text, renderStyle: 'novel', senderRole: m.role, timestamp: m.timestamp, isStreaming: streamingForThis, onButtonClick: (label) => sendQuickLabel(label) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => startEdit(m.id, (m.raw ?? m.content)), title: t('edit') || '编辑', disabled: isStreaming, children: _jsx(IconEdit, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", title: "\u4FEE\u590D", disabled: isStreaming, onClick: () => openRepairMenu(idx), children: _jsx(IconWand, { size: 14 }) }), isLast && m.role === 'assistant' && (_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => handleRegenerateAt(idx), title: t('refresh') || '重新生成', disabled: isStreaming, children: _jsx(IconRefresh, { size: 14 }) })), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => handleDeleteAt(idx), title: t('delete') || '删除', disabled: isStreaming, children: _jsx(IconTrash, { size: 14 }) })] })] }))] }, m.id));
                            }
                            return (_jsx("div", { className: `chat-message ${m.role}`, style: { alignSelf: 'stretch', width: '100%', maxWidth: '100%', padding: '2px 0', position: 'relative' }, ref: isLast && !isUser ? (el) => { if (el)
                                    lastMsgRef.current = el; } : undefined, children: (() => {
                                    const composed = (streamingForThis
                                        ? { text: m.content, pagesCount: 0 }
                                        : composedRender[idx] || { id: m.id, text: m.content, pagesCount: 0 });
                                    return (_jsxs(_Fragment, { children: [composed.pagesCount > 0 && (_jsx("div", { style: { marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }, children: _jsxs("span", { style: { fontSize: 12, opacity: 0.85 }, children: ["\u754C\u9762\u7247\u6BB5 \u00B7 ", composed.pagesCount] }) })), editingId === m.id ? (_jsxs("div", { style: { marginTop: 8 }, children: [_jsx(Textarea, { autosize: true, minRows: 2, maxRows: 10, value: editValue, onChange: (e) => setEditValue(e.currentTarget.value) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "filled", onClick: saveEdit, title: t('save') || '保存', children: _jsx(IconSend, { size: 14 }) }), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "subtle", onClick: cancelEdit, title: t('cancel') || '取消', children: _jsx(IconX, { size: 14 }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(MarkdownMessage, { content: composed.text, renderStyle: 'novel', senderRole: m.role, timestamp: m.timestamp, isStreaming: streamingForThis, onButtonClick: (label) => sendQuickLabel(label) }), _jsxs(Group, { gap: 8, mt: 6, children: [_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => startEdit(m.id, (m.raw ?? m.content)), title: t('edit') || '编辑', disabled: isStreaming, children: _jsx(IconEdit, { size: 14 }) }), isLast && m.role === 'assistant' && (_jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => handleRegenerateAt(idx), title: t('refresh') || '重新生成', disabled: isStreaming, children: _jsx(IconRefresh, { size: 14 }) })), _jsx(ActionIcon, { size: "sm", radius: "sm", variant: "light", onClick: () => handleDeleteAt(idx), title: t('delete') || '删除', disabled: isStreaming, children: _jsx(IconTrash, { size: 14 }) })] })] }))] }));
                                })() }, m.id));
                        })), _jsx("div", { ref: endRef })] }) }), _jsxs("div", { ref: floatsRef, className: "chat-floats", style: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 12px calc(max(env(safe-area-inset-bottom, 0px), var(--sab, 0px)) + 12px)', pointerEvents: 'none' }, children: [isStreaming && (_jsxs(Group, { justify: "center", gap: 6, style: {
                            padding: '6px 12px',
                            background: 'rgba(0,0,0,0.25)',
                            backdropFilter: 'blur(8px) saturate(120%)',
                            WebkitBackdropFilter: 'blur(8px) saturate(120%)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: 12,
                            margin: '0 0 6px 0',
                            pointerEvents: 'auto'
                        }, children: [_jsx(Loader, { size: "xs" }), _jsx(Text, { size: "xs", c: "dimmed", children: t('common.loading') || '生成中…' })] })), _jsx(Card, { withBorder: false, radius: 0, p: "md", className: "chat-input-card", style: {
                            background: 'transparent',
                            backdropFilter: undefined,
                            WebkitBackdropFilter: undefined,
                            border: 'none',
                            boxShadow: 'none',
                            pointerEvents: 'auto'
                        }, children: _jsx(Stack, { gap: 6, children: _jsxs(Group, { align: "flex-end", gap: "xs", wrap: "nowrap", children: [_jsx(Textarea, { id: "send_textarea", value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: handleKeyDown, placeholder: activeScriptId ? String(t('placeholder.type_message') || '') : String(t('placeholder.select_script') || ''), variant: "filled", radius: "xl", autosize: true, minRows: 1, maxRows: 6, disabled: isStreaming, style: { flex: 1, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }, styles: {
                                            input: {
                                                background: 'rgba(255,255,255,0.08)',
                                                borderColor: 'rgba(255,255,255,0.16)',
                                                color: 'inherit',
                                                backdropFilter: 'blur(6px) saturate(120%)',
                                                WebkitBackdropFilter: 'blur(6px) saturate(120%)',
                                            },
                                        } }), _jsx(ActionIcon, { variant: "filled", radius: "xl", size: "lg", onClick: handleSend, disabled: !draft.trim() || isStreaming || !authUser, style: { boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }, children: _jsx(IconSend, { size: 18 }) })] }) }) })] })] }));
};
export default ChatWindow;
